import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  Users,
  BookOpen,
  Trophy,
  AlertCircle,
  UserPlus,
  Plus,
  Trash2,
  Maximize2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  useGetClassroomStudentsStatistics,
  useGetClassroomDashboard,
  useGetClassroomAssignmentsStatistics,
  useGetClassroomAnalytics,
} from "@packages/api/madlenAPI";
import { BaseClassroomResponseDto } from "@packages/api/madlenAPI.schemas";
import { useAppSelector } from "@packages/store/hooks";
import { useLocalStorage } from "@packages/hooks/useLocalStorage";
import { ChartData, ChartProps } from "./single-analytics.type";
import { ClassSelector } from "@features/students/components/ClassSelector";
import { StatsCard } from "@features/students/components/StatsCard";
import { PerformanceChart } from "@features/students/components/charts/PerformanceChart";
import {
  StudentDistributionChart,
  StudentDistributionProps,
} from "@features/students/components/charts/StudentDistributionChart";
import {
  AssignmentProgressChart,
  AssignmentProgressProps,
} from "@features/students/components/charts/AssignmentProgressChart";
import { TopStudentsSection } from "@features/students/components/charts/TopStudentsSection";
import { StudentDetailsTable } from "@features/students/components/charts/StudentDetailsTable";
import logo from "@packages/assets/logo.png";
import { twMerge } from "tailwind-merge";
import { Button } from "@core/button";
import { trackEvent } from "@packages/hooks/useAnalytics";
import AddStudentModal from "@features/students/components/AddStudentModal";
import { CreateClassroomModal } from "@features/students/components/CreateClassroomModal";
import { StudentTable } from "@features/students/components/StudentTable";
import DeleteClassroomModal from "@features/students/components/DeleteClassroomModal";
import Modal from "@core/modal";
import { SimpleLoading } from "@core/loading";
interface EmptyStateMessageProps {
  message: string;
}

export const EmptyStateMessage: React.FC<EmptyStateMessageProps> = ({
  message,
}) => (
  <div className="flex flex-col items-center justify-center h-full p-6 text-center space-y-4">
    <div className="rounded-full bg-amber-100 p-3">
      <AlertCircle className="w-6 h-6 text-amber-600" />
    </div>
    <p className="text-gray-500 dark:text-gray-400 text-lg">{message}</p>
  </div>
);
interface CardProps {
  children: React.ReactNode;
  className?: string;
}

export const Card: React.FC<CardProps> = ({ children, className }) => {
  return (
    <div
      className={twMerge(
        "bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700",
        className,
      )}
    >
      {children}
    </div>
  );
};

type ChartComponentType =
  | React.ComponentType<ChartProps>
  | React.ComponentType<StudentDistributionProps>
  | React.ComponentType<AssignmentProgressProps>;

type ChartDataType =
  | ChartData[]
  | StudentDistributionProps["data"]
  | AssignmentProgressProps["data"];

const StudentsAnalyticsView: React.FC = () => {
  const { t } = useTranslation();
  const classrooms = useAppSelector(
    (state) => state.user.teacher?.classes || [],
  );
  const [selectedClassId, setSelectedClassId] = useLocalStorage<number | null>(
    "selected-classroom-id",
    null,
  );
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showCreateClassroomModal, setShowCreateClassroomModal] =
    useState(false);
  const [showDeleteClassroomModal, setShowDeleteClassroomModal] =
    useState(false);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [selectedChart, setSelectedChart] = useState<{
    component: ChartComponentType;
    data: ChartDataType;
  } | null>(null);

  const { trackEvent: track } = trackEvent();
  const { data: studentStats, isLoading: isLoadingStudents } =
    useGetClassroomStudentsStatistics(selectedClassId ?? 0);
  const { data: analytics } = useGetClassroomAnalytics(selectedClassId ?? 0);
  const { data: dashboard } = useGetClassroomDashboard(selectedClassId ?? 0);
  const { data: assignmentStats } = useGetClassroomAssignmentsStatistics(
    selectedClassId ?? 0,
  );

  useEffect(() => {
    const selectedClass = classrooms.find((c) => c.id === selectedClassId);
    if (classrooms?.length > 0 && (!selectedClassId || !selectedClass)) {
      const defaultClass =
        classrooms.find((c) => c.studentCount > 0) || classrooms[0];
      setSelectedClassId(defaultClass.id);
    }
  }, [classrooms, selectedClassId, setSelectedClassId]);

  const performanceData: ChartData[] = useMemo(() => {
    if (!analytics?.classAnalytics?.performanceTrends) return [];

    return analytics.classAnalytics.performanceTrends.map(
      (trend): ChartData => {
        const monthKey = trend.month || "";
        const avgCompletion = dashboard?.assignmentAvgCompletion?.[monthKey];

        return {
          month: trend.month || "",
          averageScore: trend.averageScore ?? 0,
          completionRate: trend.completionRate ?? 0,
          participationRate: trend.participationRate ?? 0,
          assignmentCompletion: avgCompletion?.avgCompletionTime ?? 0,
        };
      },
    );
  }, [analytics, dashboard]);

  const handleClickCreateClassroom = () => {
    track(
      {
        category: "ContentGeneration",
        action: "students.generate.classroom",
        label: "Students generate classroom click",
        nonInteraction: false,
      },
      {
        location: "/students-generate-classroom",
        element: "generate-button",
      },
    );
    setShowCreateClassroomModal(true);
  };

  const handleClickAddStudent = () => {
    track(
      {
        category: "ContentAddition",
        action: "students.add.student",
        label: "DailyPlannerPastPlans generation click",
        nonInteraction: false,
      },
      {
        location: "/students-add-student",
        element: "add-button",
      },
    );
    setShowAddStudentModal(true);
  };

  const handleClickDeleteClassroom = () => {
    track(
      {
        category: "ContentDeletion",
        action: "students.delete.classroom",
        label: "Students delete classroom click",
        nonInteraction: false,
      },
      {
        location: "/students-delete-classroom",
        element: "delete-button",
      },
    );
    setShowDeleteClassroomModal(true);
  };

  const handleClassroomDeleted = () => {
    // If we deleted the currently selected class, select the first available class
    if (classrooms.length > 0) {
      const firstClass =
        classrooms.find((c) => c.id !== selectedClassId) || classrooms[0];
      setSelectedClassId(firstClass.id);
    } else {
      setSelectedClassId(null);
    }
  };

  const studentDistributionData: StudentDistributionProps["data"] =
    useMemo(() => {
      const studentAverages = studentStats?.studentAverages;
      if (!studentAverages?.length) return [];

      const ranges = [
        {
          min: 90,
          max: 100,
          category: t("analytics.levels.excellent"),
          color: "#22c55e",
        },
        {
          min: 70,
          max: 89,
          category: t("analytics.levels.good"),
          color: "#3b82f6",
        },
        {
          min: 50,
          max: 69,
          category: t("analytics.levels.average"),
          color: "#f59e0b",
        },
        {
          min: 0,
          max: 49,
          category: t("analytics.levels.needsHelp"),
          color: "#ef4444",
        },
      ];

      return ranges.map((range) => {
        const count = studentAverages.filter((student) => {
          const avgPoint = student.averagePoint ?? 0;
          return avgPoint >= range.min && avgPoint <= range.max;
        }).length;

        const total = studentAverages.length || 1;
        const percentage = ((count / total) * 100).toFixed(1);

        return {
          category: range.category,
          count,
          color: range.color,
          percentage,
        };
      });
    }, [studentStats, t]);

  const processedAssignmentStats: AssignmentProgressProps["data"] =
    useMemo(() => {
      if (!assignmentStats || !dashboard?.assignmentAvgCompletion) return [];

      return Object.entries(dashboard.assignmentAvgCompletion).map(
        ([key, stat]) => ({
          title: dashboard.assignmentNames?.[key] ?? "",
          averageScore: stat.avgScore ?? 0,
          completionRate: Math.min((stat.avgCompletionTime ?? 0) / 100, 100),
          submissionDate: key,
          submissions: Object.keys(dashboard.studentAssignmentCompletion ?? {})
            .length,
        }),
      );
    }, [assignmentStats, dashboard]);

  const quickStats = useMemo(
    () => ({
      totalStudents: analytics?.classAnalytics?.quickStats?.totalStudents ?? 0,
      averageScore: analytics?.classAnalytics?.quickStats?.averageScore ?? 0,
      totalAssignments:
        analytics?.classAnalytics?.quickStats?.totalAssignments ?? 0,
      completionRate:
        analytics?.classAnalytics?.quickStats?.completionRate ?? 0,
    }),
    [analytics],
  );

  if (isLoadingStudents) {
    return <SimpleLoading fastLoading={true} />;
  }

  const renderChart = <T extends ChartDataType>(
    Component: React.ComponentType<{ data: T }>,
    data: T,
    emptyMessage: string,
  ) => {
    return (
      <Card className="p-6 relative">
        {data.length > 0 ? (
          <>
            <Button
              onClick={() => {
                setSelectedChart({
                  component: Component as ChartComponentType,
                  data,
                });
                setIsChartModalOpen(true);
              }}
              variant="ghost"
              size="icon"
              rounded="full"
              className="absolute top-4 right-4 z-10"
              bgColor="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
              leftIcon={
                <Maximize2 className="w-4 h-4 text-gray-600 dark:text-gray-300" />
              }
              aria-label={t("common.expand")}
              data-testid="expand-chart-button"
            />
            <Component data={data} />
          </>
        ) : (
          <EmptyStateMessage message={t(emptyMessage)} />
        )}
      </Card>
    );
  };

  return (
    <div className="flex-1 md:p-8 p-4 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {t("analytics.title")}
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {t("analytics.subtitle")}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row w-full sm:w-auto justify-end gap-4 mx-auto mt-4 md:mx-0 md:mt-0">
              <ClassSelector
                classes={classrooms as BaseClassroomResponseDto[]}
                selectedClass={
                  classrooms.find((c) => c.id === selectedClassId) ?? null
                }
                setSelectedClass={setSelectedClassId}
              />
              <Button
                variant="solid"
                size="md"
                bgColor="bg-primary-500"
                rounded="lg"
                textColor="text-white"
                leftIcon={<Plus size={20} />}
                onClick={handleClickCreateClassroom}
              >
                {t("students.actions.createClassroom")}
              </Button>
              <Button
                variant="solid"
                size="md"
                bgColor="bg-primary-500"
                rounded="lg"
                textColor="text-white"
                leftIcon={<UserPlus size={20} />}
                onClick={handleClickAddStudent}
              >
                {t("students.actions.add.title")}
              </Button>
              {selectedClassId && (
                <Button
                  variant="solid"
                  size="md"
                  bgColor="bg-red-500 hover:bg-red-600"
                  rounded="lg"
                  textColor="text-white"
                  leftIcon={<Trash2 size={20} />}
                  onClick={handleClickDeleteClassroom}
                >
                  {t("students.actions.deleteClassroom")}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            icon={Users}
            title={t("analytics.stats.totalStudents")}
            value={quickStats.totalStudents}
            color="blue"
          />
          <StatsCard
            icon={Activity}
            title={t("analytics.stats.averageScore")}
            value={quickStats.averageScore}
            color="green"
            showAsPercentage
          />
          <StatsCard
            icon={BookOpen}
            title={t("analytics.stats.assignments")}
            value={quickStats.totalAssignments}
            color="yellow"
          />
          <StatsCard
            icon={Trophy}
            title={t("analytics.stats.completionRate")}
            value={quickStats.completionRate}
            color="purple"
            showAsPercentage
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="lg:col-span-2">
            {renderChart<ChartData[]>(
              PerformanceChart,
              performanceData,
              "analytics.emptyStates.noPerformanceData",
            )}
          </div>
          <div className="lg:col-span-2">
            <StudentTable searchQuery={""} selectedClassId={selectedClassId!} />
          </div>
          {renderChart<StudentDistributionProps["data"]>(
            StudentDistributionChart,
            studentDistributionData,
            "analytics.emptyStates.noDistributionData",
          )}
          {renderChart<AssignmentProgressProps["data"]>(
            AssignmentProgressChart,
            processedAssignmentStats,
            "analytics.emptyStates.noAssignmentData",
          )}
        </div>

        {/* Student Sections */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          {(studentStats?.studentAverages?.length ?? 0) > 0 ? (
            <TopStudentsSection students={studentStats!.studentAverages} />
          ) : (
            <Card className="p-6">
              <EmptyStateMessage
                message={t("analytics.emptyStates.noStudentData")}
              />
            </Card>
          )}
        </div>
      </div>
      <AddStudentModal
        show={showAddStudentModal}
        onClose={() => setShowAddStudentModal(false)}
        uuid={classrooms?.find((c) => c.id === selectedClassId)?.uuid}
      />
      <CreateClassroomModal
        show={showCreateClassroomModal}
        onClose={() => setShowCreateClassroomModal(false)}
        onSubmit={(newClassroom) => {
          setSelectedClassId(newClassroom.id);
          setTimeout(() => {
            setShowCreateClassroomModal(false);
            setShowAddStudentModal(true);
          }, 50);
        }}
      />
      <DeleteClassroomModal
        show={showDeleteClassroomModal}
        onClose={() => setShowDeleteClassroomModal(false)}
        classroomId={selectedClassId}
        classroomName={
          classrooms?.find((c) => c.id === selectedClassId)?.name || ""
        }
        onDeleted={handleClassroomDeleted}
      />

      {selectedChart && (
        <Modal
          show={isChartModalOpen}
          onClose={() => setIsChartModalOpen(false)}
          outerClassName="max-w-5xl animate-modal-open"
        >
          <div className="h-[65vh] overflow-auto">
            <selectedChart.component isFullScreen data={selectedChart.data} />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default StudentsAnalyticsView;
